// src/components/Cards/ProductCard.tsx
import { formatPrice } from "@/app/utilities/helper";
import LeadButton from "@/component/ui/Lead/LeadButton";
import Link from "next/link";
import ImageWrapper from "../ImageWrapper";
import Heading from "../Heading";
import { HeadingProps } from "@/app/utilities/interfaces";

interface Car {
	_id: string;
	name: string;
	slug: string;
	brand_name: string;
	brand_slug: string;
	featured_image?: string | null | undefined;
	price_range: {
		base: number;
		top: number;
	};

	// Detection flag
	isEv?: boolean;

	// EV specs
	max_power?: string;     // e.g., "169 bhp"
	max_torque?: string;    // e.g., "255 Nm"
	range?: string;         // fallback if torque missing

	// ICE specs
	engine_displacement?: string;  // e.g., "1497 cc"
	fuel_type?: string;            // e.g., "Petrol", "Diesel"
	fuel_types?: string[];
	base_variant: any;        // e.g., "Petrol", "Diesel"
	launch_date?: string | null;   // e.g. "April 2028" or "Q2 2026"
	status?: string | null;
}

interface ProductCardProps {
	car: Car;
	href?: string;
	leadBtnText?: string;
	leadCTAHref?: string;
	trackingClass?: string;
	cardClickTrackingClass?: string;
	showBasePriceOnly?: boolean;
	showSpecs?: boolean;
	priority?: boolean;
	isMobile?: boolean;
	isEMIRoute?: boolean;
	isMileageRoute?: boolean;
	isImagesRoute?: boolean;
	index?: number;
	leadsumbittracking?: string;
	footerLinkHref?: string;
	footerLinkLabel?: string;
	headingTag?: HeadingProps["tag"];
}

export interface TransformedCar {
	_id: string;
	name: string;
	slug: string;
	brand_name: string;
	brand_slug: string;
	featured_image?: string;
	price_range: {
		base: number;
		top: number;
	};

	isEv: boolean;

	max_power?: string;
	max_torque?: string;
	engine_displacement?: string | null;
	fuel_types?: string[] | null;
	launch_date?: string | null;
	is_upcoming?: boolean;
	status?: string;
}

// Strips only the heavy PDP-only `specSections` tree out of `base_variant`,
// leaving every other field on the car untouched. Use this (instead of
// transformCar) for consumers that need fields transformCar doesn't retain
// (e.g. brand object, images[], base_variant.price) but still shouldn't ship
// the ~11KB spec table to the client.
export const stripSpecSections = (car: any): any => {
	if (!car || typeof car !== "object" || !car.base_variant) return car;

	const stripVariant = (v: any) => {
		if (!v || typeof v !== "object") return v;
		const { specSections, ...rest } = v;
		return rest;
	};

	return {
		...car,
		base_variant: Array.isArray(car.base_variant)
			? car.base_variant.map(stripVariant)
			: stripVariant(car.base_variant),
	};
};

export const transformCar = (rawCar: any): TransformedCar => {
	// Already transformed upstream (e.g. trimmed server-side before crossing
	// into a client component boundary) — pass through instead of re-deriving,
	// since the heavy base_variant.specSections this relies on is gone by then.
	if (typeof rawCar.isEv === "boolean") {
		return rawCar as TransformedCar;
	}

	const baseVariant = rawCar.base_variant;

	const isElectric =
		rawCar.fuel_types?.includes("Electric") ||
		baseVariant?.is_electric ||
		false;

	// Helper: read from pre-computed specs_range (returned on all listing calls)
	const sr = (key: string) => rawCar.specs_range?.find((s: any) => s.key === key);
	const srVal = (key: string) => { const s = sr(key); return s ? `${s.min}${s.unit ? ' ' + s.unit : ''}`.trim() : ""; };

	const engineSection = baseVariant?.specSections?.find((s: any) => s.key === "engine_and_transmission");

	const max_power =
		engineSection?.specs?.find((s: any) => s.key === "max_power")?.value ||
		srVal("max_power");

	const max_torque =
		engineSection?.specs?.find((s: any) => s.key === "max_torque")?.value ||
		srVal("max_torque");

	// null (not undefined) — this crosses into a client component as a prop,
	// and React serializes undefined as a "$undefined" sentinel in the RSC
	// payload, whereas null is a plain, cheap JSON value with the same effect.
	const engine_displacement = isElectric
		? null
		: engineSection?.specs?.find((s: any) => s.key === "engine_displacement")?.value ||
		  srVal("engine_displacement");

	return {
		_id: rawCar._id,
		name: rawCar.name,
		slug: rawCar.slug,
		brand_name: rawCar.brand_name,
		brand_slug: rawCar.brand_slug,
		featured_image: rawCar.featured_image,
		price_range: {
			base: rawCar.price_range?.base ?? rawCar.price_range?.min ?? null,
			top:  rawCar.price_range?.top  ?? rawCar.price_range?.max ?? null,
		},

		isEv: isElectric,

		max_power,
		max_torque,

		engine_displacement,
		fuel_types: isElectric ? null : rawCar.fuel_types || [],
		is_upcoming: rawCar.is_upcoming ?? rawCar.status === 'upcoming',
		status: rawCar.status || '',
		launch_date: rawCar.launch_date,
	};
};

const resolveTemplate = (template: string, car: any) => {
	if (!template) return "";
	return template
		.replace(/{brandName}/g, car.brand_name || "")
		.replace(/{brand}/g, car.brand_slug || "")
		.replace(/{brandSlug}/g, car.brand_slug || "")
		.replace(/{model}/g, car.name || "")
		.replace(/{modelSlug}/g, car.slug || "");
};

const resolveLeadCTAHref = (template: string, car: any) => {
	if (!template) return "";
	return template
		.replace(/{brand}/g, car.brand_slug || "")
		.replace(/{brandSlug}/g, car.brand_slug || "")
		.replace(/{model}/g, car.slug || "")
		.replace(/{modelSlug}/g, car.slug || "");
};

export default function ProductCard({
	car,
	leadBtnText = "Get Best Price",
	leadCTAHref,
	href,
	showBasePriceOnly = false,
	showSpecs = true,
	trackingClass = '',
	cardClickTrackingClass = '',
	priority = false,
	isMobile = false,
	isEMIRoute = false,
	isMileageRoute = false,
	isImagesRoute = false,
	index,
	leadsumbittracking,
	footerLinkHref,
	footerLinkLabel,
	headingTag = "h3"
}: ProductCardProps) {
	const transformedCar = transformCar(car)

	const title = `${transformedCar.brand_name} ${transformedCar.name}`;
	const defaultHref = `/${transformedCar.brand_slug}/${transformedCar.slug}`;
	const linkHref = href || defaultHref;
	const imageUrl =
		transformedCar.featured_image ||
		"https://via.placeholder.com/600x360?text=No+Image";

	const isElectric = transformedCar.isEv;

	// Price formatting
	const basePrice = transformedCar.price_range?.base ? formatPrice(transformedCar.price_range.base, { includeUnit: true }) : null;
	const finalPrice = basePrice || "Price on Request";

	// Helper to filter out dash and em-dash values
	const filterDash = (val?: string | null) => {
		if (!val) return "";
		const trimmed = val.trim();
		return (trimmed === "-" || trimmed === "—") ? "" : trimmed;
	};

	// Specs based on vehicle type
	let primarySpec = "";
	let secondarySpec = "";

	if (showSpecs) {
		if (isElectric) {
			// EV: Max Power • Torque (or Range)
			primarySpec = filterDash(transformedCar.max_power);
			secondarySpec = filterDash(transformedCar.max_torque);
		} else {
			// Non-EV (ICE): Engine CC • Fuel Type
			primarySpec = filterDash(transformedCar.engine_displacement);
			if (Array.isArray(transformedCar.fuel_types) && transformedCar.fuel_types.length > 0) {
				secondarySpec = transformedCar.fuel_types
					.map(filterDash)
					.filter(Boolean)
					.join(' | ');
			}
		}
	}

	const finalTag = () => {
		return car.status === 'new-launched' ? "Latest Launch" : "";
	};

	const resolvedFooterLinkHref = footerLinkHref
		? (footerLinkHref.includes("{") ? resolveTemplate(footerLinkHref, transformedCar) : defaultHref + footerLinkHref)
		: "";

	const resolvedLeadBtnText = leadBtnText.includes("{")
		? resolveTemplate(leadBtnText, transformedCar)
		: leadBtnText;
	const resolvedLeadCTAHref = leadCTAHref
		? resolveLeadCTAHref(leadCTAHref, transformedCar)
		: "";

	return (
		<div className="bg-white border border-gray-200 rounded-[10px] md:rounded-[24px] overflow-hidden h-full flex flex-col" data-testid="product-card-main">
			<Link prefetch={false} href={linkHref} className={`${cardClickTrackingClass} block relative`} title={`${car.brand_name} ${car.name}`}>
				<div className="relative w-full aspect-[11/6] bg-gray-50">
					<ImageWrapper
						src={imageUrl}
						alt={title}
						fill
						sizes="(max-width: 639px) calc(100vw - 40px), (max-width: 767px) 284px, (max-width: 1023px) 348px, (max-width: 1279px) 304px, 286px"
						unoptimized={false}
						className="w-full h-full object-cover"
						title={title}
						priority={priority}
						isMobile={isMobile}
					/>
				</div>
				{/* EV Badge */}
				{finalTag() && (
					<span className="absolute top-2 left-2 inline-flex items-center px-2.5 py-1 text-xs font-bold uppercase tracking-wide text-[#166534] bg-[#DCFCE7] border border-[#22C55E]/60 rounded-md">
						{finalTag()}
					</span>
				)}
			</Link>

			<div className="p-4 flex flex-col flex-grow">
				{/* Car Name */}
				<Link prefetch={false} href={linkHref} className={`${cardClickTrackingClass} block`} title={`${car.brand_name} ${car.name}`}>
					<Heading tag={headingTag} size={headingTag} className="!text-[18px] !font-semibold line-clamp-1" dataTestId="product-card-heading">
						{transformedCar.brand_name} {transformedCar.name}
					</Heading>
				</Link>

				{/* Price */}
				<p className="text-[14px] md:text-[16px] font-bold text-[#101828] mt-2">
					{finalPrice}
					{finalPrice !== "Price on Request" && (
						<small className="text-[12px] text-[#62748E] font-normal">  onwards</small>
					)}
				</p>
				

				{/* Specs Row */}
				<div className="mt-1 flex items-left justify-left gap-2 text-sm text-gray-700">
					{primarySpec && (
						<span
							title={isElectric ? "Max Power" : "Engine Displacement"}
							className="px-1 py-1 bg-[#F8FAFC] rounded-md font-medium text-left"
						>
							{primarySpec}
						</span>
					)}
					{secondarySpec && (
						<span title={isElectric ? (transformedCar.max_torque ? "Max Torque" : "Range") : "Fuel Type"} className="px-3 py-1 bg-[#F8FAFC] rounded-md font-medium">
							{secondarySpec}
						</span>
					)}
				</div>

				{/* CTA Button */}
				<div className="mt-4">
					{isEMIRoute || isMileageRoute || isImagesRoute ? (
						<Link
							prefetch={false}
							title={isImagesRoute
								? `${title} Images`
								: isMileageRoute
									? `Check ${title} ${isElectric ? "Range" : "Mileage"}`
									: `Check ${title} EMI`}
							href={isImagesRoute
								? `/${car.brand_slug}/${car.slug}/images`
								: isMileageRoute
									? `/${car.brand_slug}/${car.slug}/${isElectric ? "range" : "mileage"}`
									: `/${car.brand_slug}/${car.slug}/car-loan-emi-calculator`}
							className={`w-full py-2.5 text-base bg-[#008C8B] hover:opacity-90 text-white rounded-lg flex items-center justify-center gap-2 transition font-medium cursor-pointer ${trackingClass}`}
						>
							{isImagesRoute
								? `${transformedCar.name} Images`
								: isMileageRoute
									? (isElectric ? "Check Range" : "Check Mileage")
									: "Check EMI"}
						</Link>
					) : (
						<LeadButton
							carId={transformedCar._id}
							carName={title}
							btnText={resolvedLeadBtnText}
							leadCTAHref={resolvedLeadCTAHref}
							fullWidth
							className={`w-full py-2.5 text-base bg-[#008C8B] hover:opacity-90 text-white rounded-lg flex items-center justify-center gap-2 transition font-medium cursor-pointer ${trackingClass}`}
							is_upcoming={transformedCar.is_upcoming}
							leadsumbittracking={leadsumbittracking}
							datatestid="car-lead-cta-button"							
						/>
					)}
				</div>

				{resolvedFooterLinkHref && footerLinkLabel && (
					<Link
						href={resolvedFooterLinkHref}
						prefetch={false}
						className="mt-3 text-[13px] font-medium text-blue-600 hover:text-blue-700 transition flex items-center justify-center"
						title={resolveTemplate(footerLinkLabel, transformedCar)}
					>
						{resolveTemplate(footerLinkLabel, transformedCar)}
					</Link>
				)}
			</div>
		</div>
	);
};